import { t } from '@hefe/i18n';
import { Task } from '@hefe/models';
import { TasksSlice } from '@hefe/store';
import { dateFormat } from '@hefe/utils';
import { DatePicker, Form, InputNumber, Radio, Space } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../../store/store';

interface TasksAutoAssignmentWizardOptionsComponentProps {
  task: Task;
}

export default function TasksAutoAssignmentWizardOptionsComponent(
  props: TasksAutoAssignmentWizardOptionsComponentProps
) {
  const dispatch = useAppDispatch();
  const wizard = useSelector(TasksSlice.autoAssignmentWizardSelector);

  const [considerPrevAssignments, setConsiderPrevAssignments] =
    useState<number>(2);

  const onConsiderPrevAssignmentsChange = (value: number) => {
    setConsiderPrevAssignments(value);
    dispatch(
      TasksSlice.actions.setWizardOptionsConsiderPrevAssignments({
        season: value === 2,
      })
    );
  };

  return (
    <Form
      labelCol={{ span: 8 }}
      wrapperCol={{ span: 16 }}
      layout="horizontal"
      style={{ maxWidth: 600 }}
    >
      <Form.Item
        label={t(
          'tasks.auto_assignment.steps.options.number_of_assignables.title'
        )}
        name="numberOfAssigneesPerEvent"
        extra={t(
          'tasks.auto_assignment.steps.options.number_of_assignables.description'
        )}
        hasFeedback
        rules={[
          {
            required: true,
            message: t('form_erros.number'),
          },
          {
            type: 'number',
            min: 1,
            message: t('form_errors.number_min', {
              min: props.task.min_num_of_users ?? 1,
            }),
          },
          {
            type: 'number',
            max: props.task.max_num_of_users ?? 99,
            message: t('form_errors.number_max', {
              max: props.task.max_num_of_users ?? 99,
            }),
          },
        ]}
      >
        <InputNumber
          name="numberOfAssigneesPerEvent"
          max={props.task.max_num_of_users}
          min={props.task.min_num_of_users ?? 1}
          value={wizard.options.numberOfAssigneesPerEvent}
          defaultValue={wizard.options.numberOfAssigneesPerEvent}
          onChange={(value) =>
            dispatch(
              TasksSlice.actions.setWizardNumberOfAssigneesPerEvent(value ?? 1)
            )
          }
        />
      </Form.Item>
      <Form.Item
        label={t(
          'tasks.auto_assignment.steps.options.consider_prev_assignments.title'
        )}
        name="considerPrevAssignments"
        extra={t(
          'tasks.auto_assignment.steps.options.consider_prev_assignments.description'
        )}
        hasFeedback
      >
        <Radio.Group
          name="considerPrevAssignments"
          onChange={(value) =>
            onConsiderPrevAssignmentsChange(value.target.value)
          }
          defaultValue={2}
          value={considerPrevAssignments}
        >
          <Space direction="vertical">
            <Radio value={1}>
              {t(
                'tasks.auto_assignment.steps.options.consider_prev_assignments.no'
              )}
            </Radio>
            <Radio value={2}>
              {t(
                'tasks.auto_assignment.steps.options.consider_prev_assignments.current_season'
              )}
            </Radio>
            <Radio value={3}>
              {t(
                'tasks.auto_assignment.steps.options.consider_prev_assignments.custom'
              )}
              {considerPrevAssignments === 3 && (
                <DatePicker.RangePicker
                  format={dateFormat()}
                  defaultValue={[
                    dayjs(wizard.options.considerPrevAssignments?.from),
                    dayjs(wizard.options.considerPrevAssignments?.until),
                  ]}
                  value={[
                    dayjs(wizard.options.considerPrevAssignments?.from),
                    dayjs(wizard.options.considerPrevAssignments?.until),
                  ]}
                  onCalendarChange={(value) =>
                    dispatch(
                      TasksSlice.actions.setWizardOptionsConsiderPrevAssignments(
                        {
                          season: false,
                          from: value
                            ? value[0]?.format('YYYY-MM-DD')
                            : undefined,
                          till: value
                            ? value[1]?.format('YYYY-MM-DD')
                            : undefined,
                        }
                      )
                    )
                  }
                />
              )}
            </Radio>
          </Space>
        </Radio.Group>
      </Form.Item>
    </Form>
  );
}
